import React, { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import PixAndGoHeader from "@/components/molecules/PixAndGoHeader";
import PixAndGoLayout from "@/components/molecules/PixAndGoLayout";
import PixAndGoStepper from "@/components/molecules/PixAndGoStepper";
import { ReactComponent as SuccessIcon } from "@/assets/images/success.svg";
import { ReactComponent as ErrorIcon } from "@/assets/images/error.svg";
import { ReactComponent as ExpiredIcon } from "@/assets/images/transactionExpired.svg";
import i18n from "@/translate/i18n";
import usePixAndGoSession from "@/hooks/usePixAndGoSession";

function PixAndGoStatus() {
  const location = useLocation();
  const history = useHistory();

  const status = location.state?.status;
  const merchantSlug = location.state?.merchantSlug;
  const isCompleted = status === "completed";
  const isExpired = status === "expired";
  const isUserRegistrationFailed = status === "user_registration_failed";
  const isDepositFailed = status === "deposit_failed";

  const { clear: clearSession } = usePixAndGoSession(merchantSlug);

  useEffect(() => {
    i18n.changeLanguage("pt-BR");
  }, []);

  useEffect(() => {
    clearSession();
  }, [clearSession]);

  if (!status) {
    history.replace("/");
    return null;
  }

  const handleRetry = () => {
    if (merchantSlug) {
      history.push(`/pix-and-go/${merchantSlug}`);
    } else {
      history.replace("/");
    }
  };

  return (
    <PixAndGoLayout merchantSlug={merchantSlug}>
      <PixAndGoHeader />
      <PixAndGoStepper currentStep={5} />
      <main className="flex flex-col items-center justify-center flex-1 px-6 pb-12 sm:px-12">
        <div className="w-full max-w-lg text-center flex flex-col items-center gap-6">
          {isCompleted && (
            <>
              <SuccessIcon className="w-28 h-28" />
              <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold text-pag-on-background">
                  {i18n.t("pixAndGo.status.completed.title")}
                </h1>
                <p className="text-base text-pag-on-background-muted leading-relaxed">
                  {i18n.t("pixAndGo.status.completed.message")}
                </p>
              </div>
            </>
          )}

          {isExpired && (
            <>
              <ExpiredIcon className="w-28 h-28" />
              <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold text-pag-on-background">
                  {i18n.t("pixAndGo.status.expired.title")}
                </h1>
                <p className="text-base text-pag-on-background-muted leading-relaxed">
                  {i18n.t("pixAndGo.status.expired.message")}
                </p>
              </div>
              <button
                type="button"
                onClick={handleRetry}
                className="w-full py-4 rounded-full bg-pag-primary text-pag-on-primary font-bold text-base mt-2"
              >
                {i18n.t("pixAndGo.status.retry")}
              </button>
            </>
          )}

          {isUserRegistrationFailed && (
            <>
              <ErrorIcon className="w-28 h-28" />
              <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold text-pag-on-background">
                  {i18n.t("pixAndGo.status.userRegistrationFailed.title")}
                </h1>
                <p className="text-base text-pag-on-background-muted leading-relaxed">
                  {i18n.t("pixAndGo.status.userRegistrationFailed.message")}
                </p>
              </div>
            </>
          )}

          {isDepositFailed && (
            <>
              <ErrorIcon className="w-28 h-28" />
              <div className="flex flex-col gap-2">
                <h1 className="text-3xl font-bold text-pag-on-background">
                  {i18n.t("pixAndGo.status.depositFailed.title")}
                </h1>
                <p className="text-base text-pag-on-background-muted leading-relaxed">
                  {i18n.t("pixAndGo.status.depositFailed.message")}
                </p>
              </div>
            </>
          )}

          {!isCompleted &&
            !isExpired &&
            !isUserRegistrationFailed &&
            !isDepositFailed && (
              <>
                <ErrorIcon className="w-28 h-28" />
                <div className="flex flex-col gap-2">
                  <h1 className="text-3xl font-bold text-pag-on-background">
                    {i18n.t("pixAndGo.status.failed.title")}
                  </h1>
                  <p className="text-base text-pag-on-background-muted leading-relaxed">
                    {i18n.t("pixAndGo.status.failed.message")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="w-full py-4 rounded-full bg-pag-primary text-pag-on-primary font-bold text-base mt-2"
                >
                  {i18n.t("pixAndGo.status.retry")}
                </button>
              </>
            )}
        </div>
      </main>
    </PixAndGoLayout>
  );
}

export default PixAndGoStatus;
